import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { assert } from 'src/app/core/assert/assert';
import { CronJobsService } from '../../../../../core/services/cron-jobs.service';
import { SharedClockHandlerService } from '../../../../../core/services/features/time-tracking/shared-clock-handler.service';
import { SharedTimerHandlerService } from '../../../../../core/services/features/time-tracking/shared-timer-handler.service';
import { TimerStatus } from '../../../calendar-types';
import { calendarCronjobsInterval } from '../../../calendar.config';
import { CalendarService } from '../../../services/calendar.service';
import { TimeBlockDragResizeService } from '../interaction/movement/drag-and-resize/time-block-drag-resize-handling/time-block-drag-resize.service';
import { ITimeBlockComponentItem } from '../types/time-block-component-item-types';
import { IActiveTimeBlock } from './active-time-block-interfaces';
import { SharedActiveTimeBlockService } from './shared-active-time-block.service';

@Injectable()
export class ActiveTimeBlockTimerService implements IActiveTimeBlock {
  private timerSub$: Subscription | null = null;
  private activeTimeBlockSub$: Subscription | null = null;

  constructor(
    public readonly sharedTimerHandlerService: SharedTimerHandlerService,
    private readonly sharedActiveTimeBlockService: SharedActiveTimeBlockService,
    private readonly sharedClockHandlerService: SharedClockHandlerService,
    private readonly cronJobsService: CronJobsService,
    private readonly calendarService: CalendarService,
    private readonly timeBlockDragResizeService: TimeBlockDragResizeService,
  ) {}

  public registerTimeTracking(): void {
    if (this.activeTimeBlockSub$) {
      return;
    }

    this.sharedTimerHandlerService.registerTimeTracking(() => {
      this.activeTimeBlockSub$ = this.sharedTimerHandlerService.timerActiveTBChanged$
        .pipe(filter((timeBlockId) => timeBlockId !== null))
        .subscribe(() => {
          this.initUserEvents();
          this.updateActiveTimeBlockJob();
          this.toggleJob();
        });
    });
  }

  public deregisterTimeTracking(): void {
    this.timerSub$?.unsubscribe();
    this.timerSub$ = null;

    this.activeTimeBlockSub$?.unsubscribe();
    this.activeTimeBlockSub$ = null;

    this.sharedTimerHandlerService.deregisterTimeTracking();
  }

  /** After a user presses "Start", insert the active time block into the calendar view.
   * Remark: There is no need to conduct a http post request since the timer-endpoint inserts a time block into the database automatically.
   */
  public insertActiveTimeBlock(generatedTimeBlock: ITimeBlockComponentItem): number {
    return this.sharedActiveTimeBlockService.insertActiveTimeBlock(generatedTimeBlock);
  }

  /** After a user presses "Stop" or starts the tracker, replace the active time block with a new one to initialize dragging / resizing.
   * Remark: There is no need to conduct a http put request since the timer endpoint updates the time block automatically on the server.
   */
  public replaceActiveTimeBlock(): number {
    const activeTimeBlockId = this.sharedTimerHandlerService.timerActiveTBChanged$.value;
    if (activeTimeBlockId === null || activeTimeBlockId < 0) {
      return SharedActiveTimeBlockService.noActiveTimerTimeBlockId;
    }

    return this.sharedActiveTimeBlockService.replaceActiveTimeBlock(activeTimeBlockId);
  }

  public updateActiveTimeBlock(activeTimeBlockId: number): void {
    if (
      !this.sharedActiveTimeBlockService.canUpdate(activeTimeBlockId) ||
      this.calendarService.model.shiveTrackerActive
    ) {
      return;
    }

    const activeTimerTimeBlockId = this.sharedTimerHandlerService.timerActiveTBChanged$.value;
    assert(!!activeTimerTimeBlockId);

    this.sharedActiveTimeBlockService.updateActiveTimeBlock(activeTimerTimeBlockId);
  }

  private initUserEvents() {
    // Don't subscribe multiple times.
    if (this.timerSub$) {
      return;
    }

    this.timerSub$ = this.sharedTimerHandlerService.timerPosted$.subscribe((payload) => {
      const timerStatus = payload.status;
      const activeTimeBlock = payload.generatedTimeBlock;

      if (timerStatus === TimerStatus.Start) {
        this.sharedClockHandlerService.startClock(activeTimeBlock.timeBlockModel.start.getTime());
        this.insertActiveTimeBlock(activeTimeBlock);
      } else {
        this.replaceActiveTimeBlock();
      }
    });
  }

  private toggleJob() {
    const activeTimeBlockId = this.sharedTimerHandlerService.timerActiveTBChanged$.value;
    if (Number.isFinite(activeTimeBlockId) && activeTimeBlockId >= 0) {
      // Start job: Enlarging of active timer time block.
      this.sharedTimerHandlerService.enlargeActiveTimeBlockJob = this.cronJobsService.registerJob(
        this.updateActiveTimeBlockJob.bind(this),
        calendarCronjobsInterval,
      );
      return;
    }

    // No timer active, so stop job if necessary.
    this.sharedTimerHandlerService.deregisterTimeTracking();
  }

  /**
   * The method that is called periodically.
   */
  private updateActiveTimeBlockJob() {
    // Enlarge the active timer timeblock height.
    const activeTimeBlockId = this.sharedTimerHandlerService.timerActiveTBChanged$.value;
    const interactionInProgress =
      this.timeBlockDragResizeService.timeBlockDragResizeControllerService
        .dragOrResizeActionInProgress;

    // Don't enlarge if resize is in progress.
    if (!activeTimeBlockId || interactionInProgress) {
      return;
    }
    this.updateActiveTimeBlock(activeTimeBlockId);
  }
}
